/**
 * Phone numbers, before Firebase ever sees one.
 *
 * The sign-in screen used to be a single free-text field seeded with the string "+91".
 * Two things were wrong with that. A number could only be wrong in one place — the
 * country code — and that was exactly the part the user had to retype by hand, without
 * a keyboard that offers "+". And nothing checked anything: the first signal that a
 * number was malformed arrived after a reCAPTCHA round trip and an SMS quota hit, as
 * `auth/invalid-phone-number`.
 *
 * So the country is a dropdown over this table and the national part is digits only,
 * and the two are assembled here into the E.164 string the Firebase SDK requires.
 *
 * This is not libphonenumber and does not pretend to be. That library is ~145KB to tell
 * a user their number is short, which on a screen with one field is not a trade worth
 * making. What it does instead is the part that catches real mistakes: E.164's 15-digit
 * ceiling, a floor of 4, and an exact expected length for the countries this actually
 * ships to. An unknown-length country is checked against the envelope only — permissive
 * on purpose, because refusing a valid number is far worse here than accepting an
 * invalid one that the SMS then fails to reach.
 */

/** The most digits E.164 allows in a whole number, country code included. */
const E164_MAX = 15;

/** Below this, it is not a number that could reach anyone. */
const NATIONAL_MIN = 4;

export interface Country {
  /** ISO 3166-1 alpha-2. The dropdown's stable value — dial codes are not unique. */
  iso: string;
  name: string;
  /** Digits only, no "+". */
  dial: string;
  /**
   * Exact national-part lengths, when they are known and fixed. Omitted where a country
   * has a genuinely variable format; the envelope check still applies there.
   */
  lengths?: number[];
}

/**
 * Every calling code, so nobody is locked out by a list that stopped at the top twenty.
 * `lengths` is filled in only where it is a fixed, verifiable fact — a wrong entry here
 * would reject a real number, which is the one failure this file must not produce.
 */
export const COUNTRIES: Country[] = [
  { iso: "AF", name: "Afghanistan", dial: "93", lengths: [9] },
  { iso: "AL", name: "Albania", dial: "355" },
  { iso: "DZ", name: "Algeria", dial: "213", lengths: [9] },
  { iso: "AD", name: "Andorra", dial: "376", lengths: [6] },
  { iso: "AO", name: "Angola", dial: "244", lengths: [9] },
  { iso: "AG", name: "Antigua and Barbuda", dial: "1268", lengths: [7] },
  { iso: "AR", name: "Argentina", dial: "54" },
  { iso: "AM", name: "Armenia", dial: "374", lengths: [8] },
  { iso: "AW", name: "Aruba", dial: "297", lengths: [7] },
  { iso: "AU", name: "Australia", dial: "61", lengths: [9] },
  { iso: "AT", name: "Austria", dial: "43" },
  { iso: "AZ", name: "Azerbaijan", dial: "994", lengths: [9] },
  { iso: "BS", name: "Bahamas", dial: "1242", lengths: [7] },
  { iso: "BH", name: "Bahrain", dial: "973", lengths: [8] },
  { iso: "BD", name: "Bangladesh", dial: "880", lengths: [10] },
  { iso: "BB", name: "Barbados", dial: "1246", lengths: [7] },
  { iso: "BY", name: "Belarus", dial: "375", lengths: [9] },
  { iso: "BE", name: "Belgium", dial: "32", lengths: [9] },
  { iso: "BZ", name: "Belize", dial: "501", lengths: [7] },
  { iso: "BJ", name: "Benin", dial: "229", lengths: [8] },
  { iso: "BM", name: "Bermuda", dial: "1441", lengths: [7] },
  { iso: "BT", name: "Bhutan", dial: "975", lengths: [8] },
  { iso: "BO", name: "Bolivia", dial: "591", lengths: [8] },
  { iso: "BA", name: "Bosnia and Herzegovina", dial: "387", lengths: [8] },
  { iso: "BW", name: "Botswana", dial: "267", lengths: [8] },
  { iso: "BR", name: "Brazil", dial: "55", lengths: [10, 11] },
  { iso: "BN", name: "Brunei", dial: "673", lengths: [7] },
  { iso: "BG", name: "Bulgaria", dial: "359", lengths: [9] },
  { iso: "BF", name: "Burkina Faso", dial: "226", lengths: [8] },
  { iso: "BI", name: "Burundi", dial: "257", lengths: [8] },
  { iso: "KH", name: "Cambodia", dial: "855" },
  { iso: "CM", name: "Cameroon", dial: "237", lengths: [9] },
  { iso: "CA", name: "Canada", dial: "1", lengths: [10] },
  { iso: "CV", name: "Cape Verde", dial: "238", lengths: [7] },
  { iso: "KY", name: "Cayman Islands", dial: "1345", lengths: [7] },
  { iso: "CF", name: "Central African Republic", dial: "236", lengths: [8] },
  { iso: "TD", name: "Chad", dial: "235", lengths: [8] },
  { iso: "CL", name: "Chile", dial: "56", lengths: [9] },
  { iso: "CN", name: "China", dial: "86", lengths: [11] },
  { iso: "CO", name: "Colombia", dial: "57", lengths: [10] },
  { iso: "KM", name: "Comoros", dial: "269", lengths: [7] },
  { iso: "CG", name: "Congo — Brazzaville", dial: "242", lengths: [9] },
  { iso: "CD", name: "Congo — Kinshasa", dial: "243", lengths: [9] },
  { iso: "CR", name: "Costa Rica", dial: "506", lengths: [8] },
  { iso: "CI", name: "Côte d'Ivoire", dial: "225", lengths: [10] },
  { iso: "HR", name: "Croatia", dial: "385" },
  { iso: "CU", name: "Cuba", dial: "53", lengths: [8] },
  { iso: "CY", name: "Cyprus", dial: "357", lengths: [8] },
  { iso: "CZ", name: "Czechia", dial: "420", lengths: [9] },
  { iso: "DK", name: "Denmark", dial: "45", lengths: [8] },
  { iso: "DJ", name: "Djibouti", dial: "253", lengths: [8] },
  { iso: "DM", name: "Dominica", dial: "1767", lengths: [7] },
  { iso: "DO", name: "Dominican Republic", dial: "1809", lengths: [7] },
  { iso: "EC", name: "Ecuador", dial: "593", lengths: [9] },
  { iso: "EG", name: "Egypt", dial: "20", lengths: [10] },
  { iso: "SV", name: "El Salvador", dial: "503", lengths: [8] },
  { iso: "GQ", name: "Equatorial Guinea", dial: "240", lengths: [9] },
  { iso: "ER", name: "Eritrea", dial: "291", lengths: [7] },
  { iso: "EE", name: "Estonia", dial: "372" },
  { iso: "SZ", name: "Eswatini", dial: "268", lengths: [8] },
  { iso: "ET", name: "Ethiopia", dial: "251", lengths: [9] },
  { iso: "FJ", name: "Fiji", dial: "679", lengths: [7] },
  { iso: "FI", name: "Finland", dial: "358" },
  { iso: "FR", name: "France", dial: "33", lengths: [9] },
  { iso: "GA", name: "Gabon", dial: "241" },
  { iso: "GM", name: "Gambia", dial: "220", lengths: [7] },
  { iso: "GE", name: "Georgia", dial: "995", lengths: [9] },
  { iso: "DE", name: "Germany", dial: "49" },
  { iso: "GH", name: "Ghana", dial: "233", lengths: [9] },
  { iso: "GI", name: "Gibraltar", dial: "350", lengths: [8] },
  { iso: "GR", name: "Greece", dial: "30", lengths: [10] },
  { iso: "GL", name: "Greenland", dial: "299", lengths: [6] },
  { iso: "GD", name: "Grenada", dial: "1473", lengths: [7] },
  { iso: "GU", name: "Guam", dial: "1671", lengths: [7] },
  { iso: "GT", name: "Guatemala", dial: "502", lengths: [8] },
  { iso: "GN", name: "Guinea", dial: "224", lengths: [9] },
  { iso: "GW", name: "Guinea-Bissau", dial: "245", lengths: [9] },
  { iso: "GY", name: "Guyana", dial: "592", lengths: [7] },
  { iso: "HT", name: "Haiti", dial: "509", lengths: [8] },
  { iso: "HN", name: "Honduras", dial: "504", lengths: [8] },
  { iso: "HK", name: "Hong Kong", dial: "852", lengths: [8] },
  { iso: "HU", name: "Hungary", dial: "36", lengths: [9] },
  { iso: "IS", name: "Iceland", dial: "354", lengths: [7] },
  { iso: "IN", name: "India", dial: "91", lengths: [10] },
  { iso: "ID", name: "Indonesia", dial: "62" },
  { iso: "IR", name: "Iran", dial: "98", lengths: [10] },
  { iso: "IQ", name: "Iraq", dial: "964", lengths: [10] },
  { iso: "IE", name: "Ireland", dial: "353", lengths: [9] },
  { iso: "IL", name: "Israel", dial: "972", lengths: [9] },
  { iso: "IT", name: "Italy", dial: "39" },
  { iso: "JM", name: "Jamaica", dial: "1876", lengths: [7] },
  { iso: "JP", name: "Japan", dial: "81", lengths: [10] },
  { iso: "JO", name: "Jordan", dial: "962", lengths: [9] },
  { iso: "KZ", name: "Kazakhstan", dial: "7", lengths: [10] },
  { iso: "KE", name: "Kenya", dial: "254", lengths: [9] },
  { iso: "KI", name: "Kiribati", dial: "686", lengths: [5] },
  { iso: "KW", name: "Kuwait", dial: "965", lengths: [8] },
  { iso: "KG", name: "Kyrgyzstan", dial: "996", lengths: [9] },
  { iso: "LA", name: "Laos", dial: "856" },
  { iso: "LV", name: "Latvia", dial: "371", lengths: [8] },
  { iso: "LB", name: "Lebanon", dial: "961" },
  { iso: "LS", name: "Lesotho", dial: "266", lengths: [8] },
  { iso: "LR", name: "Liberia", dial: "231" },
  { iso: "LY", name: "Libya", dial: "218", lengths: [9] },
  { iso: "LI", name: "Liechtenstein", dial: "423", lengths: [7] },
  { iso: "LT", name: "Lithuania", dial: "370", lengths: [8] },
  { iso: "LU", name: "Luxembourg", dial: "352" },
  { iso: "MO", name: "Macao", dial: "853", lengths: [8] },
  { iso: "MG", name: "Madagascar", dial: "261", lengths: [9] },
  { iso: "MW", name: "Malawi", dial: "265", lengths: [9] },
  { iso: "MY", name: "Malaysia", dial: "60" },
  { iso: "MV", name: "Maldives", dial: "960", lengths: [7] },
  { iso: "ML", name: "Mali", dial: "223", lengths: [8] },
  { iso: "MT", name: "Malta", dial: "356", lengths: [8] },
  { iso: "MH", name: "Marshall Islands", dial: "692", lengths: [7] },
  { iso: "MR", name: "Mauritania", dial: "222", lengths: [8] },
  { iso: "MU", name: "Mauritius", dial: "230", lengths: [8] },
  { iso: "MX", name: "Mexico", dial: "52", lengths: [10] },
  { iso: "FM", name: "Micronesia", dial: "691", lengths: [7] },
  { iso: "MD", name: "Moldova", dial: "373", lengths: [8] },
  { iso: "MC", name: "Monaco", dial: "377" },
  { iso: "MN", name: "Mongolia", dial: "976", lengths: [8] },
  { iso: "ME", name: "Montenegro", dial: "382", lengths: [8] },
  { iso: "MA", name: "Morocco", dial: "212", lengths: [9] },
  { iso: "MZ", name: "Mozambique", dial: "258", lengths: [9] },
  { iso: "MM", name: "Myanmar", dial: "95" },
  { iso: "NA", name: "Namibia", dial: "264", lengths: [9] },
  { iso: "NP", name: "Nepal", dial: "977", lengths: [10] },
  { iso: "NL", name: "Netherlands", dial: "31", lengths: [9] },
  { iso: "NZ", name: "New Zealand", dial: "64" },
  { iso: "NI", name: "Nicaragua", dial: "505", lengths: [8] },
  { iso: "NE", name: "Niger", dial: "227", lengths: [8] },
  { iso: "NG", name: "Nigeria", dial: "234", lengths: [10] },
  { iso: "MK", name: "North Macedonia", dial: "389", lengths: [8] },
  { iso: "NO", name: "Norway", dial: "47", lengths: [8] },
  { iso: "OM", name: "Oman", dial: "968", lengths: [8] },
  { iso: "PK", name: "Pakistan", dial: "92", lengths: [10] },
  { iso: "PW", name: "Palau", dial: "680", lengths: [7] },
  { iso: "PS", name: "Palestine", dial: "970", lengths: [9] },
  { iso: "PA", name: "Panama", dial: "507", lengths: [8] },
  { iso: "PG", name: "Papua New Guinea", dial: "675", lengths: [8] },
  { iso: "PY", name: "Paraguay", dial: "595", lengths: [9] },
  { iso: "PE", name: "Peru", dial: "51", lengths: [9] },
  { iso: "PH", name: "Philippines", dial: "63", lengths: [10] },
  { iso: "PL", name: "Poland", dial: "48", lengths: [9] },
  { iso: "PT", name: "Portugal", dial: "351", lengths: [9] },
  { iso: "PR", name: "Puerto Rico", dial: "1787", lengths: [7] },
  { iso: "QA", name: "Qatar", dial: "974", lengths: [8] },
  { iso: "RO", name: "Romania", dial: "40", lengths: [9] },
  { iso: "RU", name: "Russia", dial: "7", lengths: [10] },
  { iso: "RW", name: "Rwanda", dial: "250", lengths: [9] },
  { iso: "WS", name: "Samoa", dial: "685" },
  { iso: "SM", name: "San Marino", dial: "378" },
  { iso: "SA", name: "Saudi Arabia", dial: "966", lengths: [9] },
  { iso: "SN", name: "Senegal", dial: "221", lengths: [9] },
  { iso: "RS", name: "Serbia", dial: "381" },
  { iso: "SC", name: "Seychelles", dial: "248", lengths: [7] },
  { iso: "SL", name: "Sierra Leone", dial: "232", lengths: [8] },
  { iso: "SG", name: "Singapore", dial: "65", lengths: [8] },
  { iso: "SK", name: "Slovakia", dial: "421", lengths: [9] },
  { iso: "SI", name: "Slovenia", dial: "386", lengths: [8] },
  { iso: "SB", name: "Solomon Islands", dial: "677" },
  { iso: "SO", name: "Somalia", dial: "252" },
  { iso: "ZA", name: "South Africa", dial: "27", lengths: [9] },
  { iso: "KR", name: "South Korea", dial: "82" },
  { iso: "SS", name: "South Sudan", dial: "211", lengths: [9] },
  { iso: "ES", name: "Spain", dial: "34", lengths: [9] },
  { iso: "LK", name: "Sri Lanka", dial: "94", lengths: [9] },
  { iso: "SD", name: "Sudan", dial: "249", lengths: [9] },
  { iso: "SR", name: "Suriname", dial: "597" },
  { iso: "SE", name: "Sweden", dial: "46" },
  { iso: "CH", name: "Switzerland", dial: "41", lengths: [9] },
  { iso: "SY", name: "Syria", dial: "963", lengths: [9] },
  { iso: "TW", name: "Taiwan", dial: "886", lengths: [9] },
  { iso: "TJ", name: "Tajikistan", dial: "992", lengths: [9] },
  { iso: "TZ", name: "Tanzania", dial: "255", lengths: [9] },
  { iso: "TH", name: "Thailand", dial: "66", lengths: [9] },
  { iso: "TL", name: "Timor-Leste", dial: "670", lengths: [8] },
  { iso: "TG", name: "Togo", dial: "228", lengths: [8] },
  { iso: "TO", name: "Tonga", dial: "676" },
  { iso: "TT", name: "Trinidad and Tobago", dial: "1868", lengths: [7] },
  { iso: "TN", name: "Tunisia", dial: "216", lengths: [8] },
  { iso: "TR", name: "Türkiye", dial: "90", lengths: [10] },
  { iso: "TM", name: "Turkmenistan", dial: "993", lengths: [8] },
  { iso: "UG", name: "Uganda", dial: "256", lengths: [9] },
  { iso: "UA", name: "Ukraine", dial: "380", lengths: [9] },
  { iso: "AE", name: "United Arab Emirates", dial: "971", lengths: [9] },
  { iso: "GB", name: "United Kingdom", dial: "44", lengths: [10] },
  { iso: "US", name: "United States", dial: "1", lengths: [10] },
  { iso: "UY", name: "Uruguay", dial: "598", lengths: [8] },
  { iso: "UZ", name: "Uzbekistan", dial: "998", lengths: [9] },
  { iso: "VU", name: "Vanuatu", dial: "678" },
  { iso: "VE", name: "Venezuela", dial: "58", lengths: [10] },
  { iso: "VN", name: "Vietnam", dial: "84" },
  { iso: "YE", name: "Yemen", dial: "967" },
  { iso: "ZM", name: "Zambia", dial: "260", lengths: [9] },
  { iso: "ZW", name: "Zimbabwe", dial: "263", lengths: [9] },
];

/** The one the bot was built for. */
export const DEFAULT_ISO = "IN";

/**
 * The countries Firebase will actually deliver an SMS to.
 *
 * This is not a product decision made here — it mirrors the Identity Platform SMS region
 * allowlist described in C-20, which currently permits `IN` and nothing else. A country
 * outside it fails as `auth/operation-not-allowed`, which reads exactly like the phone
 * provider being switched off, and the user has no way to tell that their country was the
 * problem.
 *
 * It exists so the dropdown cannot *default* to a country the platform will refuse. Every
 * country stays selectable — the list is not filtered — because someone travelling with a
 * foreign number is a real case and hiding their country would be worse than a failed
 * send with an error on screen.
 *
 * **Widen this and the Identity Platform allowlist together, one country at a time.**
 * Either alone is a silent failure: this list alone sends people at a provider that will
 * refuse them, and the allowlist alone changes nothing anyone can reach.
 */
export const SMS_REGIONS = new Set(["IN"]);

const BY_ISO = new Map(COUNTRIES.map((c) => [c.iso, c]));

export function countryByIso(iso: string): Country {
  return BY_ISO.get(iso) ?? BY_ISO.get(DEFAULT_ISO)!;
}

/**
 * The flag, from the ISO code, as two regional-indicator code points.
 *
 * Derived rather than stored: 200 emoji in the source would be 200 chances for one to be
 * wrong, and the mapping is arithmetic. Platforms that render no flag show the two
 * letters instead, which is a legible fallback rather than a missing glyph.
 */
export function flagOf(iso: string): string {
  return String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Everything but the digits, gone. What the national-number field is allowed to hold. */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Strip what a person pastes but a country code does not want: a leading "0" (the
 * national trunk prefix, which E.164 drops), and the dial code itself if they pasted a
 * whole international number into the national field.
 *
 * The second half needs care, and a test caught it doing damage. "9198765432" is a valid
 * ten-digit Indian number that happens to begin with India's own dial code, so removing
 * the prefix on sight turns a good number into an eight-digit one. The prefix is only
 * removed when the digits cannot be a national number as they stand — either an explicit
 * "+" says they are international, or they are longer than this country's national
 * numbers can be. When in doubt, nothing is removed: a number the user must correct is a
 * far better outcome than one silently altered behind them.
 */
export function stripNational(value: string, country: Country): string {
  const international = value.trim().startsWith("+");
  const digits = digitsOnly(value);

  const withoutDial =
    digits.startsWith(country.dial) && digits.length > country.dial.length
      ? digits.slice(country.dial.length)
      : null;

  const keepAsIs =
    withoutDial === null || (!international && fitsNationally(digits, country));

  return (keepAsIs ? digits : withoutDial).replace(/^0+/, "");
}

/** Could these digits be a national number for this country, exactly as typed? */
function fitsNationally(digits: string, country: Country): boolean {
  return country.lengths
    ? country.lengths.includes(digits.length)
    : digits.length >= NATIONAL_MIN && digits.length <= maxNationalLength(country);
}

/** The most national digits this country can hold without breaking E.164. */
export function maxNationalLength(country: Country): number {
  const cap = E164_MAX - country.dial.length;
  return country.lengths ? Math.min(cap, Math.max(...country.lengths)) : cap;
}

export type PhoneProblem = "empty" | "short" | "long" | "wrongLength";

export interface PhoneCheck {
  /** The E.164 string to hand Firebase. Present only when `problem` is null. */
  e164: string | null;
  problem: PhoneProblem | null;
  /** For "wrongLength", what this country actually expects. */
  expected: number[] | null;
}

/**
 * Is this a number worth spending an SMS on?
 *
 * Deliberately not a regex over the whole thing: the useful answer is *which* way it is
 * wrong, because "that doesn't look like a phone number" is the message this replaces.
 */
export function checkPhone(national: string, country: Country): PhoneCheck {
  const digits = digitsOnly(national);
  const fail = (problem: PhoneProblem, expected: number[] | null = null): PhoneCheck => ({
    e164: null,
    problem,
    expected,
  });

  if (digits.length === 0) return fail("empty");
  if (digits.length < NATIONAL_MIN) return fail("short");
  if (digits.length + country.dial.length > E164_MAX) return fail("long");
  if (country.lengths && !country.lengths.includes(digits.length)) {
    return fail("wrongLength", country.lengths);
  }

  return { e164: `+${country.dial}${digits}`, problem: null, expected: null };
}

/**
 * Group the national digits for reading. Purely visual — `checkPhone` runs on the raw
 * digits, so the spacing can never make a valid number invalid.
 *
 * 10 digits split 5+5 is the Indian convention and the one number the author will type a
 * hundred times; everything else falls back to threes, which is wrong for some countries
 * and unreadable for none.
 *
 * `expected` is the length the number is heading for, so a half-typed one is grouped the
 * way it will end up rather than regrouping under the caret. Without it five digits of an
 * Indian number read "987 65" and then jumped to "98765 43210" at the tenth keystroke.
 */
export function formatNational(digits: string, expected?: number): string {
  if (digits.length === 10 || expected === 10) {
    return [digits.slice(0, 5), digits.slice(5)].filter(Boolean).join(" ");
  }
  return digits.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
}

/**
 * A guess at the user's country, for the dropdown's initial value.
 *
 * The browser's locale is the only signal available without asking, and its region subtag
 * is a statement about formatting rather than location — so this is a starting position,
 * never an assumption. A wrong guess costs one tap on a dropdown.
 *
 * A guess is only taken when the SMS can actually be delivered there. A browser set to
 * en-US on a desk in Bangalore would otherwise open on "+1", and the first thing that
 * happens is a send that Firebase refuses for a reason the screen cannot explain.
 */
export function guessCountry(locale: string | undefined): string {
  const region = locale?.split("-")[1]?.toUpperCase();
  return region && BY_ISO.has(region) && SMS_REGIONS.has(region) ? region : DEFAULT_ISO;
}

/**
 * Split a stored E.164 number back into a country and a national part, for showing a
 * number someone already signed in with. Longest dial code wins, so "+1246" (Barbados)
 * is not read as "+1" followed by a national number starting "246".
 */
export function splitE164(e164: string): { country: Country; national: string } | null {
  if (!e164.startsWith("+")) return null;
  const digits = digitsOnly(e164);
  const match = [...COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((c) => digits.startsWith(c.dial));
  if (!match) return null;
  return { country: match, national: digits.slice(match.dial.length) };
}
